import styled from 'styled-components'
import { Body, Display } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'
import { type OverlapHeroData } from '@/features/cms/components/CMS/types'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Box, Column } from '@shipt/design-system-layouts'
import { theme } from '@/features/shared/constants/shiptTheme'

/**
 * This component corresponds to the `overlap_hero` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/overlap_hero
 * - **Production** - https://cms.shipt.com/content-type/pool/overlap_hero
 */
export const OverlappingHero = ({
  id = '',
  content_type_id = 'overlap_hero',
  data: { heading, subheading = '', references, background_image, hide_mobile_image = false },
}: OverlapHeroData) => {
  return (
    <Container {...getCMSWrapperProps({ content_type_id, id })} className="no-margin">
      <BackgroundImageWrapper $hideOnMobile={hide_mobile_image}>
        <BackgroundImage src={background_image.src} alt="" fill quality={100} sizes="100vw" />
      </BackgroundImageWrapper>
      <ContentWrapper>
        <Heading as="h1" size="sm">
          {heading}
        </Heading>
        {!!subheading && (
          <Box marginBottom="xl">
            <Body>{subheading}</Body>
          </Box>
        )}
        <ReferenceRendererWrapper>
          <ReferenceRenderer references={references} />
        </ReferenceRendererWrapper>
      </ContentWrapper>
    </Container>
  )
}

const Container = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 1.5rem;
  background-color: ${theme.white};

  @media ${screenSizes.max_mobile} {
    gap: 1rem;
    padding: 1rem 1rem 1.5rem;
  }
`

const BackgroundImageWrapper = styled.div<{ $hideOnMobile: boolean }>`
  position: relative;

  @media ${screenSizes.max_mobile} {
    height: 15.625rem;
    border-radius: 0.5rem;
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'block')};
  }

  @media ${screenSizes.tablet} {
    height: 22.5rem;
  }

  @media ${screenSizes.smDesktop} {
    height: 20rem;
  }

  @media ${screenSizes.lgDesktop} {
    height: 23.75rem;
  }
`

const BackgroundImage = styled(Image)`
  width: 100%;
  border-radius: 0.5rem;
  object-fit: cover;
`

const ContentWrapper = styled.div`
  isolation: isolate;
  margin: 0 auto;
  width: 100%;
  height: auto;
  padding: 2rem 1.5rem;
  display: grid;
  place-items: center;
  align-content: center;
  text-align: center;
  border-radius: 0.5rem;
  box-shadow: ${theme.shadows[1]};
  background-color: ${theme.white};

  @media ${screenSizes.max_mobile} {
    p {
      max-width: 23.75rem;
    }
  }

  @media ${screenSizes.tablet} {
    margin: -11.25rem auto 0;
    padding: 2.5rem 1.5rem;
    max-width: 35rem;
  }

  @media ${screenSizes.smDesktop} {
    margin: -10rem auto 0;
    padding: 2.5rem 4.5rem;
    max-width: 40rem;
  }
`

const Heading = styled(Display)`
  margin-bottom: 1rem;
  font-variant-ligatures: none;
`

const ReferenceRendererWrapper = styled(Column)`
  justify-content: center;
  width: 100%;
`
